import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CsvBookingRow,
  CsvImportPerformAttributesTypeEnum,
  CsvParseResult,
  CsvRow,
} from "@rentablo/api/apiv3";
import { useSecurityImages, useTableSortWithData } from "@hooks";
import { DepotParseEditModal } from "@features/csvDrop/ParsingResults/DepotParseEditModal";
import { InvestmentTypeEnum } from "@rentablo/api/apiv1";
import { Box, Typography } from "@mui/material";
import SuccessIcon from "@icons/myicons/check-circle-filled.svg?react";
import { Banner } from "@rentablo/components";
import { isRowBlocked } from "@features/csvDrop/helpers";
import { useAsyncCsvImport } from "@features/csvDrop/useAsyncCsvImport";
import { CsvImportPerformAttributesBankSlugEnum } from "@rentablo/api/apiv3";
import { ParsingResultsTable } from "./components/ParsingResultsTable";
import { ParsingResultsActions } from "./components/ParsingResultsActions";
import { AccountParseEditModal } from "./AccountParseEditModal";

const getIsNextDisabled = (
  results: CsvRow[],
  type: CsvImportPerformAttributesTypeEnum
) => {
  if (!results.length) return false;
  return results.some((row) => isRowBlocked(row, type));
};

type Props = {
  depotId?: number;
  balance?: string;
  parsingResult: CsvParseResult | null;
  changeStep: (step: number) => void;
  type: CsvImportPerformAttributesTypeEnum;
  showOnlyErrors?: boolean;
  hadCorrectionStep?: boolean;
};

export const ParsingResults = ({
  balance,
  parsingResult,
  changeStep,
  depotId,
  type,
  showOnlyErrors,
  hadCorrectionStep,
}: Props) => {
  const { t } = useTranslation();
  const [prevResult, setPrevResult] = useState(parsingResult?.result);
  const [parsingResults, setParsingResults] = useState<CsvRow[] | undefined>(
    parsingResult?.result
  );
  if (parsingResult?.result !== prevResult) {
    setPrevResult(parsingResult?.result);
    setParsingResults(parsingResult?.result);
  }
  const [parsingResultToEdit, setParsingResultToEdit] = useState<CsvRow | null>(
    null
  );

  const isCashTransactions = type === "cash_transaction";
  const { isImporting, submitImport } = useAsyncCsvImport(
    parsingResult?.slug as CsvImportPerformAttributesBankSlugEnum | undefined,
    depotId,
    type
  );

  const displayedResults = useMemo(
    () =>
      showOnlyErrors
        ? (parsingResults?.filter((row) => isRowBlocked(row, type)) ?? [])
        : (parsingResults ?? []),
    [parsingResults, showOnlyErrors, type]
  );

  const hasErrors = parsingResults?.some((row) => isRowBlocked(row, type));
  const isNextDisabled = getIsNextDisabled(parsingResults ?? [], type);

  const {
    sort,
    sortDirection,
    sortField,
    data: sortedResults,
  } = useTableSortWithData({
    data: displayedResults,
    defaultSort: {
      field: "date.value",
      direction: "ASC",
    },
  });

  const imagesData = parsingResults?.filter((result) => "isin" in result) as
    | CsvBookingRow[]
    | undefined;

  const images = useSecurityImages(
    imagesData?.map((result) => ({
      ...result,
      quote: {
        quoteProvider: "tte",
      },
      isin: result.isin?.value,
      hasValidIsin: Boolean(result.isin?.value),
      type: result.asset_type?.value ?? InvestmentTypeEnum._11_STOCK,
    }))
  );

  const handleEdit = (result: CsvRow) => {
    const updatedParsingResults = parsingResults?.map((parsingResult) =>
      parsingResult.id === result.id ? result : parsingResult
    );

    setParsingResults(updatedParsingResults);
    setParsingResultToEdit(null);
  };

  const handleDelete = (result: CsvRow) => {
    const updatedParsingResults = parsingResults?.filter(
      (parsingResult) => parsingResult.id !== result.id
    );

    setParsingResults(updatedParsingResults);
  };

  const handleNextClick = () => {
    if (showOnlyErrors) {
      changeStep(2);
    } else if (parsingResults) {
      submitImport(parsingResults, type, balance);
    } else {
      changeStep(0);
    }
  };

  return (
    <Box maxWidth={1200}>
      {showOnlyErrors && (
        <Typography
          data-testid="parsing-results-correction-hint"
          variant="body1"
          color="textSecondary"
          mb={4}
        >
          {t("csvImport.parsingResults.correctionHint")}
        </Typography>
      )}
      {showOnlyErrors && !hasErrors ? (
        <Banner
          data-testid="parsing-results-all-corrected"
          type="success"
          icon={<SuccessIcon />}
          text={t("csvImport.parsingResults.allCorrected")}
        />
      ) : (
        <ParsingResultsTable
          type={type}
          sortedResults={sortedResults}
          sortField={sortField}
          sortDirection={sortDirection}
          sort={sort}
          onEditClick={setParsingResultToEdit}
          onDeleteClick={handleDelete}
          onEdit={handleEdit}
          images={images}
        />
      )}
      <ParsingResultsActions
        isSaving={isImporting}
        isNextDisabled={isNextDisabled}
        onPrevious={() =>
          changeStep(showOnlyErrors ? 0 : hadCorrectionStep ? 1 : 0)
        }
        onSubmit={handleNextClick}
      />
      {parsingResultToEdit && isCashTransactions && (
        <AccountParseEditModal
          isOpen={Boolean(parsingResultToEdit)}
          onClose={() => setParsingResultToEdit(null)}
          row={parsingResultToEdit}
          onSave={handleEdit}
        />
      )}
      {parsingResultToEdit && !isCashTransactions && (
        <DepotParseEditModal
          isOpen={Boolean(parsingResultToEdit)}
          onClose={() => setParsingResultToEdit(null)}
          parsingResult={parsingResultToEdit}
          onSave={handleEdit}
        />
      )}
    </Box>
  );
};
